import React, { useEffect, useState } from 'react';
import keycloakAuth from '../services/KeycloakAuth';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Paper,
  LinearProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  TrendingUp,
  Receipt,
  AccountBalance,
  Payment,
  Warning,
  CheckCircle,
  Error,
  Refresh,
  AttachMoney,
  People,
  Timeline,
  Assessment,
} from '@mui/icons-material';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ChartOptions,
} from 'chart.js';
import { format } from 'date-fns';
import { dashboardService } from '../services/api';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  ChartTooltip,
  Legend
);

interface DashboardMetrics {
  totalVouchers: number;
  activeVouchers: number;
  expiredVouchers: number;
  todayRevenue: number;
  weekRevenue: number;
  monthRevenue: number;
  pendingPayments: number;
  completedPayments: number;
  pendingSettlements: number;
  completedSettlements: number;
  totalMerchants: number;
  totalRetailers: number;
  totalAgents: number;
  systemHealth: 'healthy' | 'warning' | 'critical';
  alerts: Array<{
    id: string;
    type: 'info' | 'warning' | 'error';
    message: string;
    timestamp: string;
  }>;
  recentTransactions: Array<{
    id: string;
    voucherNumber?: string | null;
    type: 'creation' | 'payment' | 'refund';
    amount: number;
    currency: string;
    status: string;
    timestamp: string;
    merchant: string;
    retailer?: string;
  }>;
  performanceMetrics: {
    avgResponseTime: number;
    successRate: number;
    totalApiCalls: number;
    errorRate: number;
  };
}

const Dashboard: React.FC = () => {
  const [username, setUsername] = useState<string>('');
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    // Fetch user info
    const fetchUserInfo = async () => {
      const userInfo = await keycloakAuth.getUserInfo();
      if (userInfo) {
        setUsername(userInfo.preferred_username || userInfo.email || 'User');
      }
    };
    fetchUserInfo();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setRefreshing(true);
      const data = await dashboardService.getMetrics();
      setMetrics(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();

    // Auto-refresh every 30 seconds
    const interval = autoRefresh ? setInterval(fetchDashboardData, 30000) : null;
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  const stats = metrics ? [
    {
      title: 'Active Vouchers',
      value: metrics.activeVouchers.toLocaleString(),
      change: '+12%',
      icon: <Receipt fontSize="large" />,
      color: '#1976d2',
    },
    {
      title: 'Pending Payments',
      value: metrics.pendingPayments.toLocaleString(),
      change: '-5%',
      icon: <Payment fontSize="large" />,
      color: '#ff9800',
    },
    {
      title: "Today's Revenue",
      value: `$${metrics.todayRevenue.toLocaleString()}`,
      change: '+18%',
      icon: <AttachMoney fontSize="large" />,
      color: '#4caf50',
    },
    {
      title: 'Pending Settlements',
      value: metrics.pendingSettlements.toLocaleString(),
      change: '0%',
      icon: <AccountBalance fontSize="large" />,
      color: '#f44336',
    },
    {
      title: 'Total Merchants',
      value: metrics.totalMerchants.toLocaleString(),
      change: '+3',
      icon: <People fontSize="large" />,
      color: '#9c27b0',
    },
    {
      title: 'Total Retailers',
      value: metrics.totalRetailers.toLocaleString(),
      change: '+8',
      icon: <People fontSize="large" />,
      color: '#00bcd4',
    },
    {
      title: 'Success Rate',
      value: `${metrics.performanceMetrics.successRate.toFixed(1)}%`,
      change: '+2.3%',
      icon: <CheckCircle fontSize="large" />,
      color: '#4caf50',
    },
    {
      title: 'Avg Response Time',
      value: `${metrics.performanceMetrics.avgResponseTime}ms`,
      change: '-12ms',
      icon: <Timeline fontSize="large" />,
      color: '#2196f3',
    },
  ] : [];

  // Chart data
  const revenueChartData = {
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: [
      {
        label: 'Revenue (USD)',
        data: [12000, 19000, 15000, 25000, 22000, 30000, 28000],
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        tension: 0.4,
      },
      {
        label: 'Revenue (ZWG)',
        data: [8000, 12000, 10000, 18000, 16000, 22000, 20000],
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        tension: 0.4,
      },
    ],
  };

  const voucherStatusData = {
    labels: ['Active', 'Paid', 'Expired', 'Refunded'],
    datasets: [
      {
        data: [300, 150, 50, 25],
        backgroundColor: [
          'rgba(54, 162, 235, 0.8)',
          'rgba(75, 192, 192, 0.8)',
          'rgba(255, 99, 132, 0.8)',
          'rgba(255, 206, 86, 0.8)',
        ],
      },
    ],
  };

  const transactionVolumeData = {
    labels: ['00:00', '04:00', '08:00', '12:00', '16:00', '20:00'],
    datasets: [
      {
        label: 'Transactions',
        data: [30, 45, 120, 180, 150, 90],
        backgroundColor: 'rgba(54, 162, 235, 0.8)',
      },
    ],
  };

  const chartOptions: ChartOptions<any> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
  };

  if (loading && !metrics) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Dashboard
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Welcome back, {username}!
          </Typography>
        </Box>
        <Box display="flex" gap={2} alignItems="center">
          <Chip
            label={metrics?.systemHealth || 'Unknown'}
            color={
              metrics?.systemHealth === 'healthy'
                ? 'success'
                : metrics?.systemHealth === 'warning'
                ? 'warning'
                : 'error'
            }
            icon={
              metrics?.systemHealth === 'healthy' ? (
                <CheckCircle />
              ) : metrics?.systemHealth === 'warning' ? (
                <Warning />
              ) : (
                <Error />
              )
            }
          />
          <Tooltip title="Refresh Dashboard">
            <IconButton onClick={fetchDashboardData} disabled={refreshing}>
              <Refresh className={refreshing ? 'rotating' : ''} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* System Alerts */}
      {metrics?.alerts && metrics.alerts.length > 0 && (
        <Box mb={3}>
          {metrics.alerts.slice(0, 3).map((alert) => (
            <Alert key={alert.id} severity={alert.type} sx={{ mb: 1 }}>
              {alert.message} - {format(new Date(alert.timestamp), 'HH:mm:ss')}
            </Alert>
          ))}
        </Box>
      )}

      {/* Key Metrics Cards */}
      <Grid container spacing={3} mb={4}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card elevation={2}>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box flex={1}>
                    <Typography color="textSecondary" gutterBottom variant="caption">
                      {stat.title}
                    </Typography>
                    <Typography variant="h5" component="div" fontWeight="bold">
                      {stat.value}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: stat.change.startsWith('+')
                          ? 'success.main'
                          : stat.change.startsWith('-')
                          ? 'error.main'
                          : 'text.secondary',
                      }}
                    >
                      {stat.change} from yesterday
                    </Typography>
                  </Box>
                  <Box sx={{ color: stat.color }}>{stat.icon}</Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, height: 400 }}>
            <Typography variant="h6" gutterBottom>
              Revenue Trend
            </Typography>
            <Box height={320}>
              <Line data={revenueChartData} options={chartOptions} />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: 400 }}>
            <Typography variant="h6" gutterBottom>
              Voucher Status Distribution
            </Typography>
            <Box height={320}>
              <Doughnut data={voucherStatusData} options={chartOptions} />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Transaction Volume and Recent Transactions */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: 400 }}>
            <Typography variant="h6" gutterBottom>
              Transaction Volume (24h)
            </Typography>
            <Box height={320}>
              <Bar data={transactionVolumeData} options={chartOptions} />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: 400 }}>
            <Typography variant="h6" gutterBottom>
              Recent Transactions
            </Typography>
            <Box sx={{ overflowX: 'auto', height: 320 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Voucher</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Time</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {metrics?.recentTransactions.map((transaction) => (
                    <TableRow key={transaction.id}>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                          {transaction.voucherNumber ? `${transaction.voucherNumber.slice(0, 8)}...` : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={transaction.type}
                          size="small"
                          color={
                            transaction.type === 'payment'
                              ? 'success'
                              : transaction.type === 'creation'
                              ? 'primary'
                              : 'warning'
                          }
                        />
                      </TableCell>
                      <TableCell align="right">
                        {transaction.currency} {transaction.amount.toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={transaction.status}
                          size="small"
                          variant="outlined"
                          color={
                            transaction.status === 'COMPLETED'
                              ? 'success'
                              : transaction.status === 'PENDING'
                              ? 'warning'
                              : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {format(new Date(transaction.timestamp), 'HH:mm:ss')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {(!metrics?.recentTransactions || metrics.recentTransactions.length === 0) && (
                <Box display="flex" justifyContent="center" alignItems="center" height={200}>
                  <Typography color="textSecondary">No recent transactions</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <style>{`
        @keyframes rotate {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        .rotating {
          animation: rotate 1s linear infinite;
        }
      `}</style>
    </Box>
  );
};

export default Dashboard;